import { TabContext, TabList, TabPanel } from '@mui/lab';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { WatchlistCategory } from '#components/Watchlist/WatchlistCategory.ts';
import { type Watchlist } from '#components/Watchlist/types.ts';
import { tt } from '#i18n/index.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';
import { WatchlistTab } from './WatchlistTab.tsx';
import { WatchlistTabPanel } from './WatchlistTabPanel.tsx';

const Container = styled('div')(() => ({
  display: 'flex',
}));

export type WatchlistTabsProps = {
  items: Watchlist['watchlistItems'];
  loading: boolean;
  setActivePreview?: (item: ActivePreview) => void;
};

export const WatchlistTabs = ({ items, loading, setActivePreview }: WatchlistTabsProps) => {
  const theme = useTheme();
  const getFirstWatchlistCategoryWithItem = useStore(store => store.getFirstWatchlistCategoryWithItem);
  const hasWatchlistItemCategory = useStore(store => store.hasWatchlistItemCategory);
  const watchlist = useStore(store => store.watchlist);
  const isSmallViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const orientation = isSmallViewport ? 'horizontal' : 'vertical';

  const [activeTab, setActiveTab] = useState<WatchlistCategory>(
    getFirstWatchlistCategoryWithItem() ?? WatchlistCategory.Upcoming,
  );

  useEffect(() => {
    if (!hasWatchlistItemCategory(activeTab)) {
      setActiveTab(getFirstWatchlistCategoryWithItem() ?? WatchlistCategory.Upcoming);
    }
    // We only want to re-execute when activeTab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, watchlist.length]);

  return (
    <Container
      sx={{
        flexDirection: 'row',
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
        },
      }}
    >
      <TabContext value={activeTab}>
        <TabList
          aria-label={tt('watchlist.tablist.ariaLabel')}
          onChange={(_, value: WatchlistCategory) => {
            setActiveTab(value);
          }}
          orientation={orientation}
          scrollButtons={false}
          sx={{
            '.MuiTabs-indicator': {
              display: watchlist.length === 0 ? 'none' : undefined,
            },
            [theme.breakpoints.down('sm')]: {
              '.MuiTabs-flexContainer': {
                flexDirection: 'row',
              },
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderColor: theme.palette.divider,
              borderRight: 'none',
            },
            borderRight: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
            minWidth: '112.2px',
          }}
          variant="scrollable"
        >
          <WatchlistTab
            disabled={!hasWatchlistItemCategory(WatchlistCategory.Upcoming) || items.UPCOMING.length === 0}
            loading={loading}
            value={WatchlistCategory.Upcoming}
          />
          <WatchlistTab
            disabled={!hasWatchlistItemCategory(WatchlistCategory.Completed) || items.COMPLETED.length === 0}
            loading={loading}
            value={WatchlistCategory.Completed}
          />
          <WatchlistTab
            disabled={!hasWatchlistItemCategory(WatchlistCategory.InProduction) || items.IN_PRODUCTION.length === 0}
            loading={loading}
            value={WatchlistCategory.InProduction}
          />
          <WatchlistTab
            disabled={!hasWatchlistItemCategory(WatchlistCategory.Planned) || items.PLANNED.length === 0}
            loading={loading}
            value={WatchlistCategory.Planned}
          />
          <WatchlistTab
            disabled={!hasWatchlistItemCategory(WatchlistCategory.Canceled) || items.CANCELED.length === 0}
            loading={loading}
            value={WatchlistCategory.Canceled}
          />
        </TabList>
        <TabPanel
          sx={{
            [theme.breakpoints.down('sm')]: {
              paddingLeft: theme.spacing(1),
              paddingRight: 0,
              paddingTop: theme.spacing(1.5),
            },
            [theme.breakpoints.down('md')]: {
              height: '350px',
            },
            flexGrow: 1,
            height: '420px',
            overflowY: loading ? 'hidden' : 'scroll',
            paddingBottom: 0,
            paddingRight: 0,
            paddingTop: 0,
          }}
          value={activeTab}
        >
          <WatchlistTabPanel
            activeCategory={activeTab}
            categoryItems={items[activeTab]}
            loading={loading}
            setActivePreview={setActivePreview}
          />
        </TabPanel>
      </TabContext>
    </Container>
  );
};
